/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { negationOptions } from './SegmentCreatorItem';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType, AQLQueryValue } from '@insights_module/adc_ql/types';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';
import { StarIconFull } from '@icons/StarIcon';

const StarIcon = tw(StarIconFull)`fill-accent-muted stroke-accent-muted -mt-0.5 w-[12px] h-[12px]`;
const RatingOption = tw.div`flex items-center gap-[4px] text-[11px] px-[6px] h-[28px]`;

const getRangeCondition = (value: AQLQueryValue): string => {
  const keys = Object.keys(value as object);
  if (keys.length === 1 && keys[0] === 'gte') return 'greater_then_or_equal';
  if (keys.length === 1 && keys[0] === 'lte') return 'less_then_or_equal';
  return 'range';
};

export const SegmentCreatorRatingScoreItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { type, negation, condition, value } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData } = useSegmentCreatorContext();
  const { dropdownOptions: typeOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'greater_then_or_equal',
      label: <Option>{t('insights.query_builder.conditions.greater_then_or_equal')}</Option>,
    },
    {
      value: 'less_then_or_equal',
      label: <Option>{t('insights.query_builder.conditions.less_then_or_equal')}</Option>,
    },
    { value: 'range', label: <Option>{t('insights.query_builder.conditions.range')}</Option> },
    { value: 'eq', label: <Option>{t('insights.query_builder.conditions.exact')}</Option> },
  ];

  const ratingOptions: IDropdownOption[] = [
    {
      value: '5',
      label: (
        <RatingOption>
          <StarIcon />5
        </RatingOption>
      ),
    },
    {
      value: '4.5',
      label: (
        <RatingOption>
          <StarIcon />
          4.5
        </RatingOption>
      ),
    },
    {
      value: '4',
      label: (
        <RatingOption>
          <StarIcon />4
        </RatingOption>
      ),
    },
    {
      value: '3.5',
      label: (
        <RatingOption>
          <StarIcon />
          3.5
        </RatingOption>
      ),
    },
    {
      value: '3',
      label: (
        <RatingOption>
          <StarIcon />3
        </RatingOption>
      ),
    },
    {
      value: '2.5',
      label: (
        <RatingOption>
          <StarIcon />
          2.5
        </RatingOption>
      ),
    },
    {
      value: '2',
      label: (
        <RatingOption>
          <StarIcon />2
        </RatingOption>
      ),
    },
    {
      value: '1.5',
      label: (
        <RatingOption>
          <StarIcon />
          1.5
        </RatingOption>
      ),
    },
    {
      value: '1',
      label: (
        <RatingOption>
          <StarIcon />1
        </RatingOption>
      ),
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    typeOptions.find((item) => item.value === type) || typeOptions[0]
  );
  const [isNegation, setIsNegation] = useState<boolean>(negation || false);
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => {
      if (condition === ('eq' as unknown as AQLQueryCondition) && item.value === 'eq') return true;
      if (
        condition === ('range' as unknown as AQLQueryCondition) &&
        item.value === getRangeCondition(value as AQLQueryValue)
      )
        return true;
    }) || conditionOptions[0]
  );

  const [lowValue, setLowValue] = useState<IDropdownOption | null>(
    // @ts-ignore
    condition === 'range' && typeof value === 'object' && value?.gte
      ? // @ts-ignore
        ratingOptions.find((item) => item.value == value?.gte)
      : null
  );
  const [highValue, setHighValue] = useState<IDropdownOption | null>(
    // @ts-ignore
    condition === 'range' && typeof value === 'object' && value?.lte
      ? // @ts-ignore
        ratingOptions.find((item) => item.value == value?.lte)
      : null
  );
  const [exactValue, setExactValue] = useState<IDropdownOption | null>(
    // @ts-ignore
    condition === 'eq' && typeof value === 'number'
      ? // @ts-ignore
        ratingOptions.find((item) => item.value == value)
      : null
  );

  const resetValues = () => {
    setLowValue(null);
    setHighValue(null);
    setExactValue(null);
  };

  useEffect(() => {
    if (selectedType && selectedType.value !== type) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (
      (selectedCondition && selectedCondition.value === 'eq' && exactValue?.value) ||
      (selectedCondition.value === 'range' && lowValue?.value && highValue?.value) ||
      (selectedCondition.value === 'greater_then_or_equal' && lowValue?.value) ||
      (selectedCondition.value === 'less_then_or_equal' && highValue?.value)
    ) {
      const condition = 'range';
      const value =
        selectedCondition.value === 'eq'
          ? {
              gte: exactValue?.value ? Number(exactValue.value) : undefined,
              lte: exactValue?.value ? Number(exactValue.value) : undefined,
            }
          : {
              gte: lowValue?.value ? Number(lowValue.value) : undefined,
              lte: highValue?.value ? Number(highValue.value) : undefined,
            };
      const segment = {
        ...segmentData,
        condition: condition as unknown as AQLQueryCondition,
        value: value,
        negation: isNegation,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, lowValue, highValue, exactValue, isNegation]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={typeOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-rating-filter-bg border border-spec-rating-filter-stroke text-spec-rating-filter-text font-semibold`}
        caretStyle={tw`fill-spec-rating-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={true}
      />

      <Dropdown
        options={negationOptions}
        value={isNegation ? negationOptions[1] : negationOptions[0]}
        onChange={(val) => setIsNegation((val as IDropdownOption).value === 'is_not')}
        isSearchable={false}
        isPresentational={true}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => {
          setSelectedCondition(val as IDropdownOption);
          resetValues();
        }}
        isSearchable={false}
        dropdownMenuStyle={tw`w-[164px]`}
        isPresentational={true}
      />

      {selectedCondition?.value === 'eq' && (
        <Dropdown
          options={ratingOptions}
          onChange={(val) => setExactValue(val as IDropdownOption)}
          value={exactValue}
          dropdownMenuStyle={tw`w-[60px]`}
        />
      )}

      {(selectedCondition.value === 'greater_then_or_equal' ||
        selectedCondition.value === 'range') && (
        <Dropdown
          options={ratingOptions}
          onChange={(val) => setLowValue(val as IDropdownOption)}
          value={lowValue}
          dropdownMenuStyle={tw`w-[60px]`}
        />
      )}

      {(selectedCondition.value === 'less_then_or_equal' ||
        selectedCondition.value === 'range') && (
        <Dropdown
          options={ratingOptions}
          onChange={(val) => setHighValue(val as IDropdownOption)}
          value={highValue}
          dropdownMenuStyle={tw`w-[60px]`}
        />
      )}
    </div>
  );
};
